// Trackside spectators (RALLY_GOAL.md §5 P1-6): where the people at a hotspot stand. Pure and
// seeded, so the layout is the same on every load and is tested in Node.
import { hash01 } from './noise';

/** Floats per person: x, y (ground), z, variant (0…VARIANTS−1), height (m), phase (0…1). */
export const PERSON_STRIDE = 6;
export const CROWD_VARIANTS = 8;
/** Closest a spectator stands to the road edge (m), as marshals would place them. */
export const CROWD_CLEARANCE = 5;
/** Clearing around a hotspot kept free of trees, rocks and bushes: half-lengths along and across the road (m). */
export const CROWD_CLEAR_ALONG = 20;
export const CROWD_CLEAR_ACROSS = 13;

/** Inside a hotspot's clearing (an ellipse stretched along the road, so the crowd can be seen on the approach). */
export function inClearing(site: CrowdSite, x: number, z: number): boolean {
  const dx = x - site.x;
  const dz = z - site.z;
  const a = (dx * site.along[0] + dz * site.along[1]) / CROWD_CLEAR_ALONG;
  const b = (dx * site.away[0] + dz * site.away[1]) / CROWD_CLEAR_ACROSS;
  return a * a + b * b < 1;
}

export interface CrowdSite {
  /** Centre of the group on the ground. */
  x: number;
  z: number;
  /** Unit vector along the road and the one pointing away from it. */
  along: [number, number];
  away: [number, number];
  width: number;
}

export interface CrowdGround {
  heightAt(x: number, z: number): number;
  /** Distance from (x, z) to the edge of the nearest road (negative on the road). */
  roadClearance(x: number, z: number): number;
}

/**
 * People for one hotspot: loose rows facing the road, about 1.4 per metre of frontage, skipping
 * spots too close to any stretch of road (hairpins fold back) or too steep to stand on.
 */
export function layoutCrowd(site: CrowdSite, seed: number, ground: CrowdGround): Float32Array {
  const out: number[] = [];
  const count = Math.round(site.width * 1.4);
  for (let k = 0; k < count; k++) {
    const r1 = hash01(k, 31, seed);
    const r2 = hash01(k, 32, seed);
    const r3 = hash01(k, 33, seed);
    const r4 = hash01(k, 34, seed);
    // Front row densest; a few stand further back on the bank.
    const row = r2 < 0.5 ? 0 : r2 < 0.85 ? 1 : 2;
    const a = (k / count - 0.5) * site.width + (r1 - 0.5) * 0.9;
    const b = row * 1.1 + (r3 - 0.5) * 0.5;
    const x = site.x + site.along[0] * a + site.away[0] * b;
    const z = site.z + site.along[1] * a + site.away[1] * b;
    if (ground.roadClearance(x, z) < CROWD_CLEARANCE) continue;
    const y = ground.heightAt(x, z);
    const slope = Math.hypot(ground.heightAt(x + 0.5, z) - ground.heightAt(x - 0.5, z), ground.heightAt(x, z + 0.5) - ground.heightAt(x, z - 0.5));
    if (slope > 0.7) continue;
    const variant = Math.floor(hash01(k, 35, seed) * CROWD_VARIANTS) % CROWD_VARIANTS;
    const height = 1.62 + 0.3 * r4;
    out.push(x, y, z, variant, height, hash01(k, 36, seed));
  }
  return new Float32Array(out);
}
